'use client';

import type { Types } from '@amplitude/unified';
import { interactionResponse } from '@zillow/yield-callback';
import { logError } from '@zg-rentals/log-error';
import { getAnalyticsErrorType } from '@zg-rentals/rentals-analytics';

import { getHotpadsIdentityReady, getHotpadsIdentitySignal } from '@/src/lib/identity/browserIdentity';
import { runWhenIdle } from '@/src/lib/utils/runWhenIdle';
import { isConsentGranted, waitForConsentApi } from '@/src/lib/datagrail/consent';
import { AMPLITUDE_API_KEY, AMPLITUDE_CONSENT_CATEGORY, getAmplitudeOptions } from './config';

type AmplitudeModule = typeof import('@amplitude/unified');

/** What Amplitude accepts as a user-property value. */
export type AmplitudeUserProperties = Record<string, Types.ValidPropertyType>;

/** Upper bound on how long the idle wait can defer the load past `load`. */
const IDLE_TIMEOUT_MS = 5000;

/** Stand-in delay for browsers without `requestIdleCallback`. */
const IDLE_FALLBACK_MS = 2000;

let amplitudeReady: Promise<AmplitudeModule | null> | undefined;

/**
 * Resolves once the page has loaded *and* the main thread has gone quiet.
 *
 * Waiting for `load` keeps the SDK's ~100 KB chunk out of the network and CPU
 * contention that decides LCP; waiting for idle after that keeps its parse and
 * init off the long-task pile that decides TBT and early INP. Nothing about
 * Amplitude is user-visible, so it has no claim on either window.
 */
function whenBrowserIdle(): Promise<void> {
  return new Promise((resolve) => {
    const schedule = () => runWhenIdle(resolve, { timeoutMs: IDLE_TIMEOUT_MS, fallbackDelayMs: IDLE_FALLBACK_MS });

    if (document.readyState === 'complete') {
      schedule();
      return;
    }
    window.addEventListener('load', schedule, { once: true });
  });
}

function getResolvedUserId(): string | undefined {
  const identity = getHotpadsIdentitySignal()?.current;
  return identity?.status === 'authenticated' ? identity.userToken : undefined;
}

async function loadAmplitude(): Promise<AmplitudeModule | null> {
  try {
    await whenBrowserIdle();
    // Fail-closed, and decided once per page load: a DataGrail that never
    // loaded, or a banner the user has not answered by now, both read as "no".
    await waitForConsentApi();
    if (!isConsentGranted(AMPLITUDE_CONSENT_CATEGORY)) return null;

    // Kept as a bare dynamic import so webpack emits the whole SDK — analytics,
    // Session Replay and Experiment plugins included — as its own async chunk.
    // A static import would put all of it in the entry bundle.
    const amplitude = await import('@amplitude/unified');
    await amplitude.initAll(AMPLITUDE_API_KEY, getAmplitudeOptions(getResolvedUserId()));

    // The synchronous peek above covers the normal case, since identity settles
    // long before the browser goes idle. This catches the stragglers without
    // making init wait on a promise that may never settle.
    //
    // Compare against the resolved id rather than testing for *any* id: when
    // `analytics.userId` is undefined, Amplitude restores the user id it
    // persisted in its own cookie, so "already set" can mean "set to the person
    // who used this browser last".
    void getHotpadsIdentityReady().then((identity) => {
      if (identity.status === 'unavailable') return;
      const resolvedUserId = identity.status === 'authenticated' ? identity.userToken : undefined;
      if (amplitude.getUserId() !== resolvedUserId) {
        amplitude.setUserId(resolvedUserId);
      }
    });

    return amplitude;
  } catch (error) {
    logError({ error, errorType: getAnalyticsErrorType('AmplitudeInitError') });
    return null;
  }
}

function ensureAmplitudeReady(): Promise<AmplitudeModule | null> {
  if (typeof window === 'undefined' || !AMPLITUDE_API_KEY) return Promise.resolve(null);
  amplitudeReady ??= loadAmplitude();
  return amplitudeReady;
}

/**
 * Runs `use` once the SDK is live, or drops it if Amplitude is disabled, denied
 * consent, or failed to load.
 *
 * The `interactionResponse` between resolution and the call is what keeps
 * emitting out of INP: awaiting readiness alone is not enough, because a
 * settled promise's continuation is a microtask and still runs before the
 * interaction's paint.
 */
function whenAmplitudeReady(use: (amplitude: AmplitudeModule) => void): void {
  void ensureAmplitudeReady()
    .then(async (amplitude) => {
      if (!amplitude) return;
      await interactionResponse();
      use(amplitude);
    })
    .catch((error) => {
      logError({ error, errorType: getAnalyticsErrorType('AmplitudeTrackError') });
    });
}

/** Schedules the deferred load. Safe to call on every mount. */
export function initializeAmplitude(): void {
  void ensureAmplitudeReady();
}

export function trackAmplitudeEvent(eventName: string, eventProperties?: Record<string, unknown>): void {
  whenAmplitudeReady((amplitude) => amplitude.track(eventName, eventProperties));
}

export function identifyAmplitudeUser(userId: string | undefined): void {
  whenAmplitudeReady((amplitude) => amplitude.setUserId(userId));
}

export function setAmplitudeUserProperties(properties: AmplitudeUserProperties): void {
  whenAmplitudeReady((amplitude) => {
    const identify = new amplitude.Identify();
    for (const [key, value] of Object.entries(properties)) {
      identify.set(key, value);
    }
    amplitude.identify(identify);
  });
}
